import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { SEO } from '@/components/SEO';
import { SkipLink } from '@/components/SkipLink';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, MapPin, User, Mail, Phone, LogIn, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ordersApi } from '@/lib/api';
import { CheckoutCustomerData } from '@/lib/woocommerce';
import { toast } from 'sonner';
import { AuthModal } from '@/components/auth/AuthModal';
import { trackInitiateCheckout, trackAddPaymentInfo, trackPurchase } from '@/lib/meta-pixel';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Schema di validazione per il form checkout
const checkoutSchema = z.object({
  firstName: z.string().min(2, 'Il nome deve contenere almeno 2 caratteri'),
  lastName: z.string().min(2, 'Il cognome deve contenere almeno 2 caratteri'),
  email: z.string().email('Email non valida'),
  phone: z.string().min(10, 'Numero di telefono non valido'),
  address1: z.string().min(5, 'Indirizzo non valido'),
  address2: z.string().optional(),
  city: z.string().min(2, 'Città non valida'),
  postcode: z.string().min(5, 'CAP non valido'),
  state: z.string().min(2, 'Provincia non valida'),
  country: z.string().default('IT'),
  note: z.string().optional(),
  paymentMethod: z.string().min(1, 'Seleziona un metodo di pagamento'),
});

type CheckoutFormValues = z.infer<typeof checkoutSchema>;

const Checkout = () => {
  const { items, getTotalPrice, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      postcode: '',
      state: '',
      country: 'IT',
      note: '',
      paymentMethod: 'woocommerce_payments',
    },
  });

  // Aggiorna il form quando l'utente fa login
  useEffect(() => {
    if (isAuthenticated && user) {
      form.setValue('firstName', user.firstName);
      form.setValue('lastName', user.lastName);
      form.setValue('email', user.email);
    }
  }, [isAuthenticated, user, form]);

  // Traccia InitiateCheckout quando si arriva alla pagina checkout con prodotti nel carrello
  useEffect(() => {
    if (items.length > 0) {
      const total = getTotalPrice();
      const contentIds = items.map(item => item.id);
      const contents = items.map(item => ({
        id: item.id,
        quantity: item.quantity,
      }));

      trackInitiateCheckout({
        value: total,
        currency: 'EUR',
        num_items: items.reduce((sum, item) => sum + item.quantity, 0),
        content_ids: contentIds,
        contents: contents,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al mount della pagina checkout

  const onSubmit = async (data: CheckoutFormValues) => {
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      navigate('/catalogo');
      return;
    }

    setIsProcessing(true);
    setIsCreatingOrder(true);

    try {
      // Prepara i dati del cliente
      const customerData: CheckoutCustomerData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address: {
          address1: data.address1,
          address2: data.address2,
          city: data.city,
          postcode: data.postcode,
          state: data.state,
          country: data.country,
        },
        note: data.note,
      };

      // Prepara i line items per l'ordine
      const lineItems = items.map((item) => ({
        product_id: parseInt(item.id),
        quantity: item.quantity,
      }));

      // Crea l'ordine WooCommerce tramite API proxy
      toast.loading('Creazione ordine in corso...', { id: 'creating-order' });
      
      const orderData = {
        payment_method: data.paymentMethod,
        payment_method_title: 'WooPayments',
        set_paid: false, // Il pagamento verrà gestito separatamente
        billing: {
          first_name: customerData.firstName,
          last_name: customerData.lastName,
          email: customerData.email,
          phone: customerData.phone,
          address_1: customerData.address.address1,
          address_2: customerData.address.address2 || '',
          city: customerData.address.city,
          postcode: customerData.address.postcode,
          state: customerData.address.state,
          country: customerData.address.country,
        },
        shipping: {
          first_name: customerData.firstName,
          last_name: customerData.lastName,
          address_1: customerData.address.address1,
          address_2: customerData.address.address2 || '',
          city: customerData.address.city,
          postcode: customerData.address.postcode,
          state: customerData.address.state,
          country: customerData.address.country,
        },
        line_items: lineItems,
        customer_note: customerData.note || '',
      };
      
      // Traccia AddPaymentInfo quando viene selezionato il metodo di pagamento
      const total = getTotalPrice();
      const contentIds = items.map(item => item.id);
      const contents = items.map(item => ({
        id: item.id,
        quantity: item.quantity,
      }));

      trackAddPaymentInfo({
        value: total,
        currency: 'EUR',
        content_ids: contentIds,
        contents: contents,
      });

      const order = await ordersApi.createOrder(orderData);

      toast.dismiss('creating-order');
      toast.success('Ordine creato con successo!', { id: 'order-created' });
      
      setIsCreatingOrder(false);

      // Traccia Purchase quando l'ordine viene creato con successo
      trackPurchase({
        value: total,
        currency: 'EUR',
        content_ids: contentIds,
        contents: contents,
        num_items: items.reduce((sum, item) => sum + item.quantity, 0),
        order_id: order.id.toString(),
      });

      // Ottieni l'URL di pagamento
      toast.loading('Preparazione pagamento...', { id: 'preparing-payment' });
      
      const paymentData = await ordersApi.getOrderPaymentUrl(order.id);
      
      toast.dismiss('preparing-payment');
      
      // Svuota il carrello locale
      clearCart();
      
      // Reindirizza al pagamento WooPayments
      window.location.href = paymentData.payment_url;
      
    } catch (error) {
      console.error('Errore nel checkout:', error);
      toast.dismiss();
      toast.error('Errore durante il checkout. Riprova.', {
        description: error instanceof Error ? error.message : 'Errore sconosciuto',
      });
      setIsProcessing(false);
      setIsCreatingOrder(false);
    }
  };

  if (items.length === 0) {
    return (
      <>
        <SkipLink />
        <main id="main-content" className="min-h-screen bg-background">
          <SEO 
            title="Checkout - Brace e Sapori Store"
            description="Completa il tuo ordine inserendo i dati di spedizione e fatturazione. Pagamento sicuro con WooPayments."
            noindex={true}
          />
          <Header />
          <div className="pt-24 pb-16 px-4 md:px-6">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="mb-4 text-2xl font-bold">Il tuo carrello è vuoto</h1>
              <Button onClick={() => navigate('/catalogo')}>
                Vai al Catalogo
              </Button>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const subtotal = getTotalPrice();
  const shipping = 0; // Calcolato da WooCommerce
  const total = subtotal + shipping;

  return (
    <>
      <SkipLink />
      <main id="main-content" className="min-h-screen bg-background">
        <SEO title="Checkout - Brace e Sapori Store" />
        <Header />
        
        <div className="pt-24 pb-16 px-4 md:px-6">
          <div className="mx-auto max-w-6xl">
            <h1 className="mb-8 text-4xl font-bold">Checkout</h1>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid gap-8 lg:grid-cols-2">
                  {/* Form Dati Cliente */}
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <User className="h-5 w-5" />
                          Dati Personali
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Nome *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Mario" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Cognome *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Rossi" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email *
                              </FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="mario.rossi@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Telefono *
                              </FormLabel>
                              <FormControl>
                                <Input type="tel" placeholder="+39 123 456 7890" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MapPin className="h-5 w-5" />
                          Indirizzo di Spedizione
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={form.control}
                          name="address1"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Indirizzo *</FormLabel>
                              <FormControl>
                                <Input placeholder="Via Roma, 123" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="address2"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Indirizzo secondario (opzionale)</FormLabel>
                              <FormControl>
                                <Input placeholder="Appartamento, scala, piano..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="city"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Città *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Roma" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="postcode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>CAP *</FormLabel>
                                <FormControl>
                                  <Input placeholder="00100" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Provincia *</FormLabel>
                                <FormControl>
                                  <Input placeholder="RM" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="country"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Paese *</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Seleziona paese" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="IT">Italia</SelectItem>
                                    <SelectItem value="US">Stati Uniti</SelectItem>
                                    <SelectItem value="GB">Regno Unito</SelectItem>
                                    <SelectItem value="FR">Francia</SelectItem>
                                    <SelectItem value="DE">Germania</SelectItem>
                                    <SelectItem value="ES">Spagna</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Note Ordine (Opzionale)</CardTitle>
                        <CardDescription>
                          Aggiungi note speciali per il tuo ordine
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <FormField
                          control={form.control}
                          name="note"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Textarea
                                  placeholder="Es: Consegna al piano, campanello rotto..."
                                  rows={4}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  </div>

                  {/* Riepilogo Ordine */}
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Riepilogo Ordine</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          {items.map((item) => (
                            <div key={item.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-3">
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="h-16 w-16 rounded-md object-cover"
                                />
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  <p className="text-muted-foreground">
                                    {item.quantity} x €{item.price.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                              <span className="font-semibold">
                                €{(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        <Separator />
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotale</span>
                            <span>€{subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Spedizione</span>
                            <span className="text-muted-foreground">Calcolata al pagamento</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between text-lg font-bold">
                            <span>Totale</span>
                            <span>€{total.toFixed(2)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          Metodo di Pagamento
                        </CardTitle>
                        <CardDescription>
                          Completerai il pagamento nella pagina successiva
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <FormField
                          control={form.control}
                          name="paymentMethod"
                          render={({ field }) => (
                            <FormItem>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleziona metodo di pagamento" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="woocommerce_payments">
                                    Carta di Credito/Debito (WooPayments)
                                  </SelectItem>
                                  <SelectItem value="bacs">Bonifico Bancario</SelectItem>
                                  <SelectItem value="cod">Contrassegno</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={true} // Temporaneamente disabilitato
                    >
                      <CreditCard className="mr-2 h-5 w-5" />
                      Procedi al Pagamento (Temporaneamente Disabilitato)
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>

        <Footer />
        
        {/* Auth Modal */}
        <AuthModal open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
      </main>
    </>
  );
};

export default Checkout;
